'use client'

import clsx from 'clsx'
import { useState } from 'react'

import ActionTaskIcon from '@/assets/icons/action-task.svg'
import CreateTaskIcon from '@/assets/icons/create-task.svg'
import WatchMetaEyeIcon from '@/assets/icons/eye-svgrepo-com.svg'
import NotificationIcon from '@/assets/icons/notification-outline.svg'
import ShareIcon from '@/assets/icons/share.svg'
import TaskCardApIcon from '@/assets/icons/task-card-ap.svg'
import type { TaskCardCta } from '@/features/tasks/taskCardCta'
import Image from '@/ui/MediaImage'

import { resolveTaskCoverSrc } from '../TasksTaskCards/tasksTaskCards.constants'
import S from './ComingSoonTaskCard.module.scss'

export type ComingSoonTaskCardProps = {
  className?: string
  coverSrc?: string | null
  brandName: string
  title: string
  description: string
  pointsValue: string
  timeLeftLabel: string
  badgeLabel: string
  notifyAriaLabel: string
  ctaVariant?: TaskCardCta
  ctaLabel: string
}

export function ComingSoonTaskCard({
  className,
  coverSrc = null,
  brandName: _brandName,
  title,
  description,
  pointsValue,
  timeLeftLabel,
  badgeLabel,
  notifyAriaLabel,
  ctaVariant = 'watch',
  ctaLabel
}: ComingSoonTaskCardProps) {
  const coverImageSrc = resolveTaskCoverSrc(coverSrc)
  const [isNotifyOn, setIsNotifyOn] = useState(false)

  const ctaClassName =
    ctaVariant === 'share'
      ? S.ctaShare
      : ctaVariant === 'action'
      ? S.ctaAction
      : ctaVariant === 'create'
      ? S.ctaCreate
      : S.ctaWatch

  return (
    <article className={clsx(S.card, className)}>
      <div className={S.top}>
        <div className={S.coverWrap} aria-hidden>
          <Image
            src={coverImageSrc}
            alt=""
            fill
            className={S.coverBlurred}
            sizes="(max-width: 768px) 100vw, (max-width: 992px) 50vw, 25vw"
            priority={false}
          />
          <div className={S.coverGradient} />
        </div>
        <div className={S.brandRow}>
          <div className={S.logo}>
            <Image
              src="/assets/tasks/tasks-card/logo_default.webp"
              alt=""
              fill
              sizes="64px"
              quality={90}
              className={S.logoImg}
            />
          </div>
          <button
            type="button"
            className={S.notifyBtn}
            aria-label={notifyAriaLabel}
            aria-pressed={isNotifyOn}
            onClick={() => setIsNotifyOn(prev => !prev)}
          >
            <NotificationIcon className={S.notifyIcon} aria-hidden />
          </button>
        </div>
        <div className={S.chip}>
          <p className={S.chipText}>{badgeLabel}</p>
        </div>
      </div>
      <div className={S.body}>
        <div className={S.textBlurWrap}>
          <h3 className={S.title}>{title}</h3>
          <p className={S.description}>{description}</p>
        </div>
        <div className={S.footer}>
          <div className={S.footerGlow} aria-hidden />
          <div className={S.footerInner}>
            <div className={S.metaLeft}>
              <div className={S.apBlock}>
                <TaskCardApIcon className={S.apIcon} aria-hidden />
                <p className={S.points}>{pointsValue}</p>
              </div>
              <p className={S.timeLeft}>{timeLeftLabel}</p>
            </div>
            <button
              type="button"
              className={clsx(S.cta, ctaClassName)}
              aria-disabled="true"
              tabIndex={-1}
            >
              {ctaVariant === 'share' ? (
                <ShareIcon
                  className={clsx(S.ctaIcon, S.ctaIconShare)}
                  aria-hidden
                />
              ) : ctaVariant === 'action' ? (
                <ActionTaskIcon
                  className={clsx(S.ctaIcon, S.ctaIconAction)}
                  aria-hidden
                />
              ) : ctaVariant === 'create' ? (
                <CreateTaskIcon
                  className={clsx(S.ctaIcon, S.ctaIconCreate)}
                  aria-hidden
                />
              ) : (
                <WatchMetaEyeIcon
                  className={clsx(S.ctaIcon, S.ctaIconWatch)}
                  aria-hidden
                />
              )}
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </article>
  )
}
